/**
 * Minimal read-only JSON tree for the toolbar's User/Config panels.
 *
 * Objects/arrays are collapsible; primitives render inline with type-aware
 * coloring. Deliberately dependency-free and small — this ships in the
 * internal-tools chunk and never touches a regular user's bundle.
 */
import React, { useState } from 'react'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface JsonNodeProps {
  readonly name: string
  readonly value: unknown
  readonly depth: number
  readonly defaultOpen: boolean
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function valueType(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

function toEntries(value: unknown): Array<[string, unknown]> {
  if (Array.isArray(value)) return value.map((v, i) => [String(i), v])
  if (isRecord(value)) return Object.entries(value)
  return [['value', value]]
}

const Primitive: React.FC<{ value: unknown }> = ({ value }) => {
  const type = valueType(value)
  const text = typeof value === 'string' ? `"${value}"` : String(value)
  return <span className={cx('json-value', `json-${type}`)}>{text}</span>
}

const JsonNode: React.FC<JsonNodeProps> = ({
  name,
  value,
  depth,
  defaultOpen,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const branch = isRecord(value) || Array.isArray(value)

  if (!branch) {
    return (
      <div className={cx('json-row')} style={{ paddingLeft: depth * 12 }}>
        <span className={cx('json-key')}>{name}</span>
        <span className={cx('json-colon')}>:</span>
        <Primitive value={value} />
      </div>
    )
  }

  const entries: Array<[string, unknown]> = toEntries(value)
  const count = entries.length
  const summary = Array.isArray(value) ? `[${count}]` : `{${count}}`

  return (
    <div>
      <button
        type='button'
        className={cx('json-row', 'json-branch')}
        style={{ paddingLeft: depth * 12 }}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={cx('json-caret', { open })}>›</span>
        <span className={cx('json-key')}>{name}</span>
        <span className={cx('json-summary')}>{summary}</span>
      </button>
      {open &&
        entries.map(([k, v]) => (
          <JsonNode
            key={k}
            name={k}
            value={v}
            depth={depth + 1}
            defaultOpen={false}
          />
        ))}
    </div>
  )
}

export interface JsonViewProps {
  readonly data: unknown
  /** Open the top-level entries by default. */
  readonly defaultOpen?: boolean
}

export const JsonView: React.FC<JsonViewProps> = ({
  data,
  defaultOpen = true,
}) => {
  if (data === undefined || data === null) {
    return <div className={cx('panel-empty')}>No data serialized.</div>
  }
  const entries = toEntries(data)

  if (entries.length === 0) {
    return <div className={cx('panel-empty')}>Empty.</div>
  }

  return (
    <div className={cx('json-tree')}>
      {entries.map(([k, v]) => (
        <JsonNode
          key={k}
          name={k}
          value={v}
          depth={0}
          defaultOpen={defaultOpen}
        />
      ))}
    </div>
  )
}
